// SKADE COP — innloggings-/registreringsskjerm (GNÅ-design). Vises før kartet
// når brukeren ikke har en gyldig sesjon. To modus:
//   * Logg inn  — bruker-id + passord
//   * Registrer — enrolment-kode (fra lagfører/stab) + velg bruker-id + passord
// Ren DOM, ingen rammeverk. All feiltekst kommer fra hubben (aldri stille feil).
//
// ══ UX-REVISJON 2026-07-29 («infanterisikker»-testen) — hva som ble endret ══
//
// 1. AUTOFYLL: SAMARBEID, IKKE KAMP. Feltene sto med `autocomplete="off"` og
//    uten `name`. Chrome IGNORERER `off` på innloggingsfelt og legger sin egen
//    passordbehandler-overlay oppå etter egne gjetninger — og utvidelser
//    (LastPass o.l.) injiserer egne bokser. Det er slik et «teknisk ødelagt»
//    passordfelt oppstår hos en bruker selv om vår DOM er korrekt (reprodusert:
//    feltet er helt i ren Chromium, både desktop og mobil — skaden kommer fra
//    overlays som slåss med skjemaet). Riktige `name`/`autocomplete`-verdier
//    (`username`, `current-password`/`new-password`, `one-time-code`) gjør at
//    nettleseren VET hva feltene er og tegner sin UI forutsigbart — og at
//    lagrede passord faktisk tilbys, som er den beste «glemt passord»-
//    forebyggingen som finnes.
// 2. NØDUTGANG → SELVBETJENT RESET (QA-018). Enroll oppretter alltid en NY
//    bruker, så det var ingen vei videre når passordet var glemt — den ærlige
//    utgangen var å si NØYAKTIG hvem man kontakter. Nå finnes en reell reset:
//    en autorisert lagfører/stab (samme RBAC-nivå som utsteder enrolment-koder,
//    se admin.ts) utsteder en engangs reset-kode for DIN EKSISTERENDE
//    bruker-id — org/rolle røres ikke. Nødutgangen sier fortsatt hvem man
//    kontakter for KODEN (den utstedelsen krever fortsatt et menneske og et
//    RBAC-nivå), men innløsningen — sette det nye passordet — gjør brukeren nå
//    selv, her, uten å måtte ringe en gang til.
// 3. MENNESKESPRÅK. «HttpOnly-cookie» er en utvikler som snakker til en
//    utvikler. Setningen sier nå det samme på norsk uten å kreve fagkunnskap.
// 4. KODE UTEN TASTING. Enrolment-koden kan komme via lenke (`?enroll=KODE` —
//    lagfører sender lenke/QR som åpner appen med koden ferdig utfylt) eller
//    skannes med kameraet (BarcodeDetector der nettleseren har den, med ÆRLIG
//    beskjed der den ikke har den). Å taste 32 tilfeldige tegn i mørket med
//    hansker er det motsatte av infanterisikkert.
// 5. FØRSTEGANGSVEILEDNING. En ny bruker vet ikke hvilken fane han skal velge.
//    Én linje under fanene sier det.
import type { User } from '@skade/rbac';
import { gnaUserIdFromPhone, isMatrixUserId, normalizeMatrixId } from '@skade/contracts';
import { login, enrollStart, enrollComplete, resetPassword } from './api.js';

type Mode = 'login' | 'enroll' | 'reset';
/** Registrering er BUNDET i to trinn (SENS-24 funn 1): «start» ber om en
 *  bekreftelseskode over GNÅ; «complete» tar den koden + passord og oppretter
 *  kontoen. Uten to trinn kunne en videresendt enrolment-kode brukes av
 *  hvem som helst — se `EnrolmentStore.begin`/`complete` i hubben. */
type EnrollFase = 'start' | 'complete';

/** Hjemmeserver-domenet GNÅ-kontoer ligger på (samme som admin-panelet). */
const GNA_DOMENE = 'matrix.follodefence.com';

/** Samme tolerante tolkning som admin-panelets mottakerfelt: telefonnummer,
 *  localpart eller full @id — alt som IKKE gir en gyldig Matrix-id → ''. */
function tolkGnaIdentitet(raa: string): string {
  const t = raa.trim();
  if (!t) return '';
  const fraNummer = gnaUserIdFromPhone(t, GNA_DOMENE);
  if (fraNummer) return fraNummer;
  const normalisert = normalizeMatrixId(t, GNA_DOMENE);
  return normalisert && isMatrixUserId(normalisert) ? normalisert : '';
}

/** Menneskelig trygghetslinje — INGEN tekniske ord. Testen låser at den holder
 *  seg fri for sjargong (HttpOnly/cookie/token/…): det er lett å «presisere»
 *  den tilbake til utviklerspråk i en senere endring. */
export const TRYGGHETSLINJE =
  'Innloggingen er sikret, og ingenting sendes ut av KO-noden.';

/** Nødutgangen: hvem man kontakter for en reset-KODE (selve utstedelsen krever
 *  fortsatt et menneske med RBAC-nivå) — men innløsningen (sette nytt passord)
 *  gjør brukeren selv, i «Glemt passord?» nedenfor. Ikke lenger en død lenke. */
export const NODUTGANG =
  'Glemt passordet? Be lagføreren din eller KO-vakta om en reset-kode, så setter du selv nytt passord under «Glemt passord?».';

/** Førstegangshjelpen under fanene. */
export const FORSTEGANG = 'Første gang på denne enheten? Velg «Registrer enhet».';

/**
 * Les enrolment-kode fra URL-ens query (`?enroll=KODE`). Ren funksjon (testbar).
 * Koden valideres LØST her (base64url-aktige tegn, fornuftig lengde) — den
 * autoritative kontrollen er hubbens, dette stopper bare åpenbart rusk fra å
 * lande i feltet og se ut som en ekte kode.
 */
export function lesEnrollKode(search: string): string | null {
  const m = /[?&]enroll=([A-Za-z0-9_-]{8,128})(?:&|$)/.exec(search);
  return m ? m[1] : null;
}

export function showLogin(root: HTMLElement, onAuthed: (user: User) => void, notice?: string) {
  root.innerHTML = '';
  root.className = 'skade-login-root';
  let mode: Mode = 'login';

  const card = div('login-card');
  const brand = div('login-brand');
  brand.append(span('login-logo', 'SKADE'), span('login-tagline', 'Felles situasjonsbilde — Heimevernet'));
  card.append(brand);

  const tabs = div('login-tabs');
  const tabLogin = tab('Logg inn', true);
  const tabEnroll = tab('Registrer enhet', false);
  tabs.append(tabLogin, tabEnroll);
  card.append(tabs);

  // Førstegangshjelpen: synlig på Logg inn-fanen (der en ny bruker lander),
  // borte på Registrer-fanen (der har han allerede valgt riktig).
  const forstegang = div('login-forstegang');
  forstegang.textContent = FORSTEGANG;
  card.append(forstegang);

  const form = document.createElement('form');
  form.className = 'login-form';
  // BEVISST ingen `autocomplete="off"` på skjemaet — se filhodet, punkt 1.

  // `one-time-code`: nettleseren vet at dette er en engangskode (og kan på
  // mobil tilby den rett fra SMS). `name` gjør feltet identifiserbart for
  // passordbehandlere så de IKKE gjetter og legger overlay på feil felt.
  const codeField = field('Enrolment-kode', 'text', 'Kode fra din lagfører/stab', {
    name: 'enrolment-code', autocomplete: 'one-time-code',
  });
  const userField = field('Bruker-id', 'text', 'f.eks. ola.nordmann', {
    name: 'username', autocomplete: 'username',
  });
  // Registrering har INGEN bruker-id-felt: den utledes av GNÅ-identiteten
  // (§4 anti-spoof — se skadeUserIdFraGnaMatrixId i hubben). Det brukeren
  // faktisk oppgir er HVEM HEN PÅSTÅR Å VÆRE, så hubben kan sende
  // bekreftelseskoden til RIKTIG GNÅ-konto — samme tolerante felt som
  // admin-panelets mottakerfelt (telefonnummer, localpart eller full @id).
  const subjectField = field('Din GNÅ-identitet', 'text', 'Telefonnummer eller @gna-id', {
    name: 'gna-identitet', autocomplete: 'off',
  });
  const nonceField = field('Bekreftelseskode fra GNÅ', 'text', '6 siffer', {
    name: 'enrolment-nonce', autocomplete: 'one-time-code',
  });
  const passField = field('Passord', 'password', 'Minst 10 tegn', {
    name: 'password', autocomplete: 'current-password',
  });

  // ── QA-018: selvbetjent innløsning av en reset-kode. Bruker-id fylles ut
  // av brukeren selv (samme skjema uansett hvem det er — ingen adressat-
  // utledning som i enroll, siden reset PEKER på en allerede navngitt konto).
  const resetUserField = field('Bruker-id', 'text', 'f.eks. ola.nordmann', {
    name: 'reset-userid', autocomplete: 'username',
  });
  const resetCodeField = field('Reset-kode', 'text', 'Kode fra din lagfører/stab', {
    name: 'reset-code', autocomplete: 'one-time-code',
  });
  const resetPassField = field('Nytt passord', 'password', 'Minst 10 tegn', {
    name: 'reset-password', autocomplete: 'new-password',
  });

  // ── Vis/skjul passord. I mørket, med hansker, er blindskrift av et langt
  // passord hovedårsaken til «feil passord»-runder. Knappen er tekst («Vis»),
  // ikke et ikon som må tolkes. aria-pressed for skjermleser. ──
  const reveal = document.createElement('button');
  reveal.type = 'button';
  reveal.className = 'login-reveal';
  reveal.textContent = 'Vis';
  reveal.setAttribute('aria-label', 'Vis passordet i klartekst');
  reveal.setAttribute('aria-pressed', 'false');
  reveal.onclick = () => {
    const vis = passField.input.type === 'password';
    passField.input.type = vis ? 'text' : 'password';
    reveal.textContent = vis ? 'Skjul' : 'Vis';
    reveal.setAttribute('aria-pressed', String(vis));
    passField.input.focus();
  };
  passField.wrap.classList.add('har-reveal');
  passField.wrap.append(reveal);

  // ── QR-skanning av enrolment-koden. Feature-detektert: finnes ikke
  // BarcodeDetector (eller kamera nektes), sier vi det ÆRLIG i stedet for å
  // vise en knapp som ikke gjør noe. ──
  const skann = document.createElement('button');
  skann.type = 'button';
  skann.className = 'login-skann';
  skann.textContent = 'Skann QR';
  skann.setAttribute('aria-label', 'Skann enrolment-koden fra en QR-kode med kameraet');
  codeField.wrap.classList.add('har-skann');
  codeField.wrap.append(skann);
  let skanner: { stopp: () => void } | null = null;

  codeField.wrap.style.display = 'none';
  subjectField.wrap.style.display = 'none';
  nonceField.wrap.style.display = 'none';
  resetUserField.wrap.style.display = 'none';
  resetCodeField.wrap.style.display = 'none';
  resetPassField.wrap.style.display = 'none';

  const errEl = div('login-error');
  errEl.style.display = 'none';
  if (notice) { errEl.textContent = notice; errEl.style.display = 'block'; errEl.classList.add('notice'); }

  const submit = document.createElement('button');
  submit.type = 'submit';
  submit.className = 'login-submit';
  submit.textContent = 'Logg inn';

  // Kun synlig i registrer-fanens andre trinn: gå tilbake og be om en ny
  // bekreftelseskode (feil identitet tastet, eller koden ble ikke mottatt).
  const tilbake = document.createElement('button');
  tilbake.type = 'button';
  tilbake.className = 'login-tilbake';
  tilbake.textContent = '‹ Send bekreftelseskode på nytt';
  tilbake.style.display = 'none';

  // Nødutgangen — rett under innloggingsknappen, alltid synlig i login-modus.
  // Sier hvem man kontakter for KODEN (den utstedelsen krever fortsatt et
  // RBAC-nivå); «Glemt passord?»-lenken under fører til selve innløsningen.
  const nod = div('login-nodutgang');
  nod.textContent = NODUTGANG;

  // QA-018: den ekte selvbetjente innløsningen — bytter til reset-modus.
  const glemtPassord = document.createElement('button');
  glemtPassord.type = 'button';
  glemtPassord.className = 'login-glemt';
  glemtPassord.textContent = 'Glemt passord?';

  // Tilbake fra reset-modus til innlogging.
  const resetTilbake = document.createElement('button');
  resetTilbake.type = 'button';
  resetTilbake.className = 'login-tilbake';
  resetTilbake.textContent = '‹ Tilbake til innlogging';
  resetTilbake.style.display = 'none';

  const hint = div('login-hint');
  hint.textContent = TRYGGHETSLINJE;

  form.append(
    codeField.wrap, subjectField.wrap, nonceField.wrap, userField.wrap,
    resetUserField.wrap, resetCodeField.wrap,
    passField.wrap, resetPassField.wrap,
    errEl, submit, tilbake, resetTilbake, nod, glemtPassord, hint,
  );
  card.append(form);
  root.append(card);

  // Registrering er bundet i to trinn — se `EnrollFase`. `fase` er kun
  // meningsfull når `mode === 'enroll'`.
  let fase: EnrollFase = 'start';

  function setMode(m: Mode) {
    mode = m;
    fase = 'start';
    const isEnroll = m === 'enroll';
    const isReset = m === 'reset';
    // Fanene og førstegangshjelpen gjelder bare login/enroll — reset-modus er
    // en egen liten skjerm nådd via «Glemt passord?», ikke en tredje fane.
    tabs.style.display = isReset ? 'none' : '';
    forstegang.style.display = isEnroll || isReset ? 'none' : '';
    nod.style.display = isReset ? 'none' : '';
    glemtPassord.style.display = isReset ? 'none' : '';
    resetTilbake.style.display = isReset ? '' : 'none';
    if (!isReset) {
      tabLogin.classList.toggle('active', !isEnroll);
      tabEnroll.classList.toggle('active', isEnroll);
    }
    userField.wrap.style.display = isEnroll || isReset ? 'none' : '';
    resetUserField.wrap.style.display = isReset ? '' : 'none';
    resetCodeField.wrap.style.display = isReset ? '' : 'none';
    resetPassField.wrap.style.display = isReset ? '' : 'none';
    passField.input.setAttribute('autocomplete', isEnroll ? 'new-password' : 'current-password');
    clearErr();
    if (skanner) { skanner.stopp(); skanner = null; }
    if (isReset) {
      codeField.wrap.style.display = 'none';
      subjectField.wrap.style.display = 'none';
      nonceField.wrap.style.display = 'none';
      passField.wrap.style.display = 'none';
      tilbake.style.display = 'none';
      submit.textContent = 'Sett nytt passord';
      resetUserField.input.focus();
      return;
    }
    setFase('start');
    if (isEnroll) (codeField.input.value ? subjectField : codeField).input.focus();
    else userField.input.focus();
  }

  /** Bytt mellom registreringens to trinn (kun relevant i enroll-mode). */
  function setFase(f: EnrollFase) {
    fase = f;
    const isEnroll = mode === 'enroll';
    const erStart = f === 'start';
    codeField.wrap.style.display = isEnroll ? '' : 'none';
    codeField.input.readOnly = isEnroll && !erStart; // låst etter at koden er sendt til GNÅ
    subjectField.wrap.style.display = isEnroll && erStart ? '' : 'none';
    nonceField.wrap.style.display = isEnroll && !erStart ? '' : 'none';
    passField.wrap.style.display = !isEnroll || !erStart ? '' : 'none';
    tilbake.style.display = isEnroll && !erStart ? '' : 'none';
    submit.textContent = !isEnroll ? 'Logg inn' : erStart ? 'Send bekreftelseskode' : 'Bekreft og registrer';
    if (isEnroll && !erStart) nonceField.input.focus();
  }
  tabLogin.onclick = () => setMode('login');
  tabEnroll.onclick = () => setMode('enroll');
  tilbake.onclick = () => { setFase('start'); clearErr(); };
  glemtPassord.onclick = () => setMode('reset');
  resetTilbake.onclick = () => setMode('login');

  function clearErr() { errEl.style.display = 'none'; errEl.classList.remove('notice'); }
  function showErr(msg: string) { errEl.textContent = msg; errEl.classList.remove('notice'); errEl.style.display = 'block'; }

  skann.onclick = async () => {
    if (skanner) { skanner.stopp(); skanner = null; return; }
    skanner = await startQrSkann(codeField.wrap, (kode) => {
      codeField.input.value = kode;
      skanner = null;
      subjectField.input.focus();
    }, showErr);
  };

  async function medLaas(midlertidigTekst: string, arbeid: () => Promise<void>) {
    submit.disabled = true;
    const prev = submit.textContent;
    submit.textContent = midlertidigTekst;
    try { await arbeid(); } finally { submit.disabled = false; submit.textContent = prev; }
  }

  form.onsubmit = async (e) => {
    e.preventDefault();
    clearErr();

    if (mode === 'login') {
      const userId = userField.input.value.trim();
      const pass = passField.input.value;
      if (!userId || !pass) return showErr('Fyll ut bruker-id og passord.');
      return medLaas('Kobler til …', async () => {
        const res = await login(userId, pass);
        if (res.ok) onAuthed(res.user);
        else showErr(res.error);
      });
    }

    // ── QA-018: selvbetjent innløsning av en reset-kode ──
    if (mode === 'reset') {
      const userId = resetUserField.input.value.trim();
      const code = resetCodeField.input.value.trim();
      const pass = resetPassField.input.value;
      if (!userId || !code || !pass) return showErr('Fyll ut bruker-id, reset-kode og nytt passord.');
      return medLaas('Setter passord …', async () => {
        const res = await resetPassword(code, userId, pass);
        if (res.ok) onAuthed(res.user);
        else showErr(res.error);
      });
    }

    // ── Registrering, trinn 1: send bekreftelseskode over GNÅ ──
    if (fase === 'start') {
      const code = codeField.input.value.trim();
      const raaIdentitet = subjectField.input.value.trim();
      if (!code) return showErr('Fyll ut enrolment-koden.');
      const subject = tolkGnaIdentitet(raaIdentitet);
      if (!subject) {
        return showErr(raaIdentitet
          ? 'Kjenner ikke igjen dette som et telefonnummer eller en GNÅ-id.'
          : 'Fyll ut din GNÅ-identitet (telefonnummer eller @gna-id).');
      }
      return medLaas('Sender kode …', async () => {
        const res = await enrollStart(code, subject);
        if (res.ok) {
          setFase('complete');
          errEl.textContent = 'Bekreftelseskode sendt til GNÅ. Sjekk meldingene dine og lim den inn her.';
          errEl.style.display = 'block'; errEl.classList.add('notice');
        } else showErr(res.error);
      });
    }

    // ── Registrering, trinn 2: bekreftelseskode + nytt passord ──
    const nonce = nonceField.input.value.trim();
    const pass = passField.input.value;
    if (!nonce || !pass) return showErr('Fyll ut bekreftelseskoden og velg et passord.');
    return medLaas('Bekrefter …', async () => {
      const res = await enrollComplete(codeField.input.value.trim(), nonce, pass);
      if (res.ok) onAuthed(res.user);
      else showErr(res.error);
    });
  };

  // ── Lenke-/QR-flyten: `?enroll=KODE` i URL-en åpner Registrer-fanen med
  // koden ferdig utfylt. Koden FJERNES fra adresselinja med én gang
  // (replaceState) — en engangskode skal ikke bli liggende i historikk,
  // bokmerker eller «del side». ──
  const urlKode = lesEnrollKode(location.search);
  if (urlKode) {
    codeField.input.value = urlKode;
    const u = new URL(location.href);
    u.searchParams.delete('enroll');
    history.replaceState(null, '', u.pathname + u.search + u.hash);
    setMode('enroll');
  } else {
    userField.input.focus();
  }
}

// ── QR-skanning ─────────────────────────────────────────────────────────────
//
// BarcodeDetector er innebygd i Chromium (desktop + Android). Der den mangler
// (eldre Safari m.fl.) viser vi en ærlig beskjed i stedet for en død knapp —
// å love skanning som ikke finnes er verre enn å be om innliming.
interface QrDeteksjon { rawValue: string }
interface QrDetektor { detect(kilde: HTMLVideoElement): Promise<QrDeteksjon[]> }

async function startQrSkann(
  vert: HTMLElement,
  paaKode: (kode: string) => void,
  paaFeil: (msg: string) => void,
): Promise<{ stopp: () => void } | null> {
  const Detektor = (globalThis as Record<string, unknown>)['BarcodeDetector'] as
    | (new (o: { formats: string[] }) => QrDetektor)
    | undefined;
  if (!Detektor) {
    paaFeil('QR-skanning støttes ikke i denne nettleseren. Lim inn koden manuelt, eller åpne lenken fra lagføreren.');
    return null;
  }
  let strom: MediaStream;
  try {
    strom = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
  } catch {
    paaFeil('Fikk ikke tilgang til kameraet. Lim inn koden manuelt.');
    return null;
  }
  const video = document.createElement('video');
  video.className = 'login-qr-video';
  video.setAttribute('aria-label', 'Kamerabilde — hold QR-koden foran kameraet');
  video.srcObject = strom;
  video.muted = true;
  video.playsInline = true;
  vert.append(video);
  await video.play().catch(() => { /* stanses av stopp() uansett */ });

  const detektor = new Detektor({ formats: ['qr_code'] });
  let aktiv = true;
  const stopp = () => {
    aktiv = false;
    for (const t of strom.getTracks()) t.stop();
    video.remove();
  };
  (async () => {
    while (aktiv) {
      try {
        const funn = await detektor.detect(video);
        const kode = funn[0]?.rawValue?.trim();
        if (kode) {
          // Koden kan være rå, eller en hel lenke med ?enroll= — godta begge.
          paaKode(lesEnrollKode(`?enroll=${kode}`) ? kode : (lesEnrollKode(new URL(kode, location.href).search) ?? kode));
          stopp();
          return;
        }
      } catch { /* enkeltbilde feilet — prøv neste */ }
      await new Promise((r) => setTimeout(r, 300));
    }
  })();
  return { stopp };
}

// ── DOM-hjelpere ──────────────────────────────────────────────────────────
function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function tab(text: string, active: boolean): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'login-tab' + (active ? ' active' : '');
  b.textContent = text;
  return b;
}
function field(
  labelText: string,
  type: string,
  placeholder: string,
  auto?: { name: string; autocomplete: string },
) {
  const wrap = div('login-field');
  const lab = document.createElement('label');
  lab.textContent = labelText;
  const input = document.createElement('input');
  input.type = type;
  input.placeholder = placeholder;
  if (auto) { input.name = auto.name; input.autocomplete = auto.autocomplete as typeof input.autocomplete; }
  const id = 'f-' + labelText.toLowerCase().replace(/[^a-z]/g, '');
  input.id = id; lab.htmlFor = id;
  wrap.append(lab, input);
  return { wrap, input };
}
